import { type ReactElement, type PropsWithChildren, useEffect } from 'react';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import { newRelicNoticeError, nrErrorMap } from '@core/services/newRelic';

type ReturnType = {
  SubmissionNotificationEA: (props: PropsWithChildren) => ReactElement;
  SchoolEnrollmentStatusEA: (props: PropsWithChildren) => ReactElement;
  CtaTitleEA: (props: PropsWithChildren) => ReactElement;
  CtaSubtitleEA: (props: PropsWithChildren) => ReactElement;
  CtaLabelEA: (props: PropsWithChildren) => ReactElement;
  dualCtaEnabled: boolean;
  ctaLabelCopyEA: string | undefined;
  isTestEnabled: boolean;
  dualCtaText?: string;
  dualCtaLinkout?: string;
};

const useStartYourApplicationEditableAsset = (schoolId: string = ''): ReturnType => {
  const flags = useFeatureFlags();
  const allSchoolsData = flags.startYourApplicationSectionEditable?.all;
  const individualSchoolData = flags.startYourApplicationSectionEditable?.[schoolId];

  const data = individualSchoolData ?? allSchoolsData;

  const isTestEnabled = !!data;
  const ctaLabel = individualSchoolData?.ctaLabel ?? allSchoolsData?.ctaLabel;

  const dualCtaText = individualSchoolData?.dualCtaText ?? allSchoolsData?.dualCtaText;
  const dualCtaLinkout = individualSchoolData?.dualCtaLinkout;

  const dualCtaEnabled = String(individualSchoolData?.dualCta).toLowerCase() === 'true'; // no fallback to all

  // Attach NR alert for missing or incorrect parameters and build the EA component
  const buildEAComponent =
    (testValue?: string) =>
    // use the test value if the test is enabled and the value is present else return the children (control behavior)
    ({ children }: PropsWithChildren) =>
      <>{isTestEnabled && testValue ? testValue : children}</>;

  useEffect(() => {
    const hasAtLeastOneParam = !!Object.values(data ?? {})?.length;

    if (isTestEnabled && !hasAtLeastOneParam) {
      newRelicNoticeError(
        nrErrorMap.EDITABLE_ASSET,
        new Error(`Missing or incorrect parameter in editable asset: startYourApplicationSectionEditable`),
        {
          startYourApplicationSectionEditable: flags.startYourApplicationSectionEditable,
        }
      );
    }
  }, [data, flags.startYourApplicationSectionEditable, isTestEnabled]);

  return {
    SubmissionNotificationEA: buildEAComponent(data?.submissionNotification),
    SchoolEnrollmentStatusEA: buildEAComponent(data?.schoolEnrollmentStatus),
    CtaTitleEA: buildEAComponent(data?.ctaTitle),
    CtaSubtitleEA: buildEAComponent(data?.ctaSubtitle),
    CtaLabelEA: buildEAComponent(ctaLabel),
    dualCtaEnabled,
    ctaLabelCopyEA: ctaLabel,
    isTestEnabled,
    dualCtaText,
    dualCtaLinkout,
  };
};

export default useStartYourApplicationEditableAsset;
